import { useReply } from "@/stores/reply";
import { entityStoreFactory } from "@/stores/factory/entity-store-factory";
import { CoinRank, CoinRankItem, OveralCoinRankItem } from "@/types/coinRank";
import {
  cursorEncoderById,
  cursorPagerStoreFactory,
} from "@/stores/factory/list-store-factory";
import {
  CoinRanItemPageFilterType,
  findOneCoinRank,
  readCoinRankItemPage,
  readCoinRankPage,
  readOverallCoinRank,
} from "@/apis/coinRank";
import { CoinBalanceRecordEntityType } from "@/types/coin";
import { getServerTime } from "@/modules/common";
import i18n from "@/locales";
import { computed, ref } from "vue";
import moment from "moment-timezone";

const dateTypeKey = (id: number, entityType: CoinBalanceRecordEntityType) =>
  `${id}-${entityType}`;
export const getRankServerDtStart = (dtStart: string) =>
  getServerTime(dtStart).format("YYYY-MM-DD");

export const useCoinRankPage = () => {
  const replyStore = useReply();
  /** 历史榜单列表 */
  const historyCoinRankEntityStore = entityStoreFactory<CoinRank>("id");
  const { updateMap: updateHistoryCoinRankMap } = historyCoinRankEntityStore;
  const updateHistoryCoinRankAndReplyMap = (coinRankList: CoinRank[]) => {
    // 更新Rank和Reply
    coinRankList.forEach((item) => {
      replyStore.updateMap([item.reply]);
      updateHistoryCoinRankMap([item]);
    });
  };
  const {
    dataList: historyCoinRankList,
    initListIfAbsent: getOrFetchHistoryCoinRank,
  } = cursorPagerStoreFactory({
    entityStore: historyCoinRankEntityStore,
    fetch: async () => {
      const response = await readCoinRankPage({
        pagination: { page: 1, perPage: 999 },
        sort: { field: "dtStart", order: "desc" },
      });
      updateHistoryCoinRankAndReplyMap(response.page.data);
      return response.page.data;
    },
    cursorEncoder: cursorEncoderById,
  });

  return {
    historyCoinRankList,
    getOrFetchHistoryCoinRank,
    updateHistoryCoinRankAndReplyMap,
    updateHistoryCoinRankMap,
  };
};

export const useOverallCoinRankPage = () => {
  /** 总榜单列表 */
  const overallCoinRankEntityStore =
    entityStoreFactory<OveralCoinRankItem>("entityId");
  const { updateMap: updateOverallCoinRankMap } = overallCoinRankEntityStore;
  const nextCursor = ref<string | undefined | null>(undefined);

  const {
    dataList: overallCoinRankList,
    initListIfAbsent: getOrFetchOverallCoinRank,
    fetchNewerList: fetchNewerOverallCoinRank,
    fetchOlderList: fetchOlderOverallCoinRank,
  } = cursorPagerStoreFactory({
    entityStore: overallCoinRankEntityStore,
    fetch: async (cursor?: string) => {
      const response = await readOverallCoinRank({
        entityType: "mate",
        perPage: 20,
        cursor: nextCursor.value ?? undefined,
      });
      updateOverallCoinRankMap(response.data.list);
      nextCursor.value = response.data.nextCursor;
      return response.data.list;
    },
    cursorEncoder: cursorEncoderById,
  });

  return {
    nextCursor,
    overallCoinRankList,
    getOrFetchOverallCoinRank,
    updateOverallCoinRankMap,
    fetchNewerOverallCoinRank,
    fetchOlderOverallCoinRank,
  };
};

export const useCoinRankItemPage = () => {
  const coinRankItemMapStore = entityStoreFactory<CoinRankItem>("id");
  const { updateMap: updateCoinRankItemMap } = coinRankItemMapStore;
  /** 榜单状态维护 */
  const coinRankMapStore = entityStoreFactory<CoinRank>((rank) =>
    dateTypeKey(rank.id, rank.entityType!)
  );
  const { updateMap: updateCoinRankMap, map: coinRankMap } = coinRankMapStore;
  const uniCoinRankMapStore = entityStoreFactory<CoinRank>("id");
  const { updateMap: updateUniCoinRankMap, map: uniCoinRankMap } =
    uniCoinRankMapStore;

  const updateCoinRankAndItemMap = (
    coinRankList: CoinRank[],
    isUni = false
  ): CoinRank[] => {
    return coinRankList.map((item) => {
      // 1. 更新表单项的实体
      const rankItems = updateCoinRankItemMap(item.coinRankItemzz);
      // 2. 更新排名
      const sortedRankItems = rankItems
        .filter((item) => item.voteCount > 0)
        .sort((a, b) => b.voteCount - a.voteCount)
        .reduce((acc, item, index) => {
          if (index === 0) {
            item.position = 1;
          } else {
            const prevItem = acc[index - 1];
            if (prevItem.voteCount === item.voteCount) {
              item.position = prevItem.position;
            } else {
              item.position = index + 1;
            }
          }
          acc.push(item);
          return acc;
        }, [] as CoinRankItem[]);
      if (isUni) {
        return updateUniCoinRankMap([
          { ...item, coinRankItemzz: sortedRankItems },
        ])[0];
      } else {
        return updateCoinRankMap([
          { ...item, coinRankItemzz: sortedRankItems },
        ])[0];
      }
    });
  };

  const getSubRank = (id: number, entityType: CoinBalanceRecordEntityType) =>
    coinRankMap.value[dateTypeKey(id, entityType)];

  const getOrFetchCoinRankByEntityType = async (
    id: number,
    entityType: CoinBalanceRecordEntityType,
    force = false
  ): Promise<CoinRank> => {
    const key = dateTypeKey(id, entityType);
    const item = coinRankMap.value[key];
    if (item && !force) {
      return item;
    }
    const response = await findOneCoinRank(id, { entityType });
    return updateCoinRankAndItemMap([{ ...response.item, entityType }])[0];
  };

  const getOrFetchUniCoinRank = async (
    id: number,
    force = false
  ): Promise<CoinRank> => {
    const item = coinRankMap.value[id];
    if (item && !force) {
      return item;
    }
    const response = await findOneCoinRank(id);
    return updateCoinRankAndItemMap([response.item], true)[0];
  };

  /** 作品在某个排行榜的排名项 */
  const fetchRankItem = async (
    entityType: CoinBalanceRecordEntityType,
    entityId: number,
    id: number
  ) => {
    const filter: CoinRanItemPageFilterType = { coinRankId: id };
    switch (entityType) {
      case CoinBalanceRecordEntityType.Game:
        filter.galGameId = entityId;
        break;
      case CoinBalanceRecordEntityType.Mate:
        filter.mateId = entityId;
        break;
      case CoinBalanceRecordEntityType.StoryBook:
        filter.storyBookId = entityId;
        break;
      case CoinBalanceRecordEntityType.Collection:
        filter.collectionId = entityId;
        break;
    }
    const res = await readCoinRankItemPage({
      filter,
    });
    return res.page.data[0];
  };

  const currentCoinRank = ref<CoinRank | null>(null);
  const currentCoinRankDtStart = computed(() =>
    currentCoinRank.value
      ? getRankServerDtStart(currentCoinRank.value.dtStart)
      : ""
  );
  const getOrFetchCurrentRank = async (force = false) => {
    if (currentCoinRank.value && !force) return currentCoinRank.value;
    const response = await findOneCoinRank(0, {
      entityType: CoinBalanceRecordEntityType.Game,
    });
    currentCoinRank.value = response.item;
  };

  return {
    currentCoinRank,
    currentCoinRankDtStart,
    coinRankMapStore,
    uniCoinRankMap,
    getOrFetchCurrentRank,
    getOrFetchCoinRankByEntityType,
    fetchRankItem,
    getSubRank,
    getOrFetchUniCoinRank,
  };
};

export const useRankMoment = (dtStart: string) => {
  let dt = getServerTime(dtStart);
  if (
    dt.get("year") === 2024 &&
    dt.get("month") === 2 &&
    dt.get("date") === 16
  ) {
    dt = dt.add(1, "month").set("date", 1);
  }
  if (dt.get("date") > 25) {
    dt = dt.add(1, "month").set("date", 1);
  }
  return dt;
};

/**
 * 获取榜单的上半期/下半期文本
 * @param dtStart
 * @returns zh-CN: 上/下; en: First Half/Second Half
 */
export const useRankHalfText = (dtStart: string) => {
  const dt = useRankMoment(dtStart);
  let half = i18n.global.t("rank.firstHalf");
  if (dt.get("date") > 1) {
    half = i18n.global.t("rank.secondHalf");
  }
  return half;
};

/**
 * 获取榜单的文本
 * @param rank
 * @returns zh-CN: 2024年4月（上）; en: Apr, 2024 (First Half)
 */
export const useRankText = (rank: CoinRank) => {
  const isChinese = i18n.global.locale.value === "zh-CN";
  if (isChinese) {
    return rank.name;
  } else {
    return rank.nameEnglish;
  }
  // const dt = useRankMoment(dtStart);
  // const half = useRankHalfText(dtStart);
  // const isChinese = i18n.global.locale.value === "zh-CN";
  // if (isChinese) {
  //   return `${dt.format("YYYY年MM月")}（${half}）`;
  // } else {
  //   return `${dt.format("MMM, YYYY")} (${half})`;
  // }
};

export const rankStaticPath = `${
  import.meta.env.VITE_STATIC_URL_PREFIX
}static/rank-banner`;

/**
 * 获取榜单的Banner图片地址
 * @param type
 * @param dtStart
 * @returns Banner图片地址
 */
export const getBannerUrl = (
  type: "story" | "game" | "mate" | "collection" | "uni",
  dtStart: string
): string => {
  const isChinese = i18n.global.locale.value === "zh-CN";
  const momentStr = getServerTime(dtStart).format("YYYYMMDD");
  if (type === "uni") {
    return `${rankStaticPath}/${momentStr}/${
      isChinese ? "zh" : "en"
    }/banner.jpg`;
  } else {
    return `${rankStaticPath}/${momentStr}/${
      isChinese ? "zh" : "en"
    }/${type}.jpg`;
  }
};

export const isUniRank = (rank: CoinRank) =>
  getServerTime(rank.dtStart).isAfter(moment("2024-05-16"));
